import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Smartphone, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      // Navigate to first accessible page (avoid infinite redirect for employees without dashboard)
      const perms = userData.permissions || [];
      if (userData.role === "admin" || perms.includes("dashboard")) {
        navigate("/");
      } else if (perms.includes("repairs")) {
        navigate("/repairs");
      } else if (perms.includes("clients")) {
        navigate("/clients");
      } else if (perms.includes("inventory")) {
        navigate("/inventory");
      } else if (perms.includes("invoices")) {
        navigate("/invoices");
      } else {
        navigate("/no-access");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1639776739297-f7e1f21526f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxlbGVjdHJvbmljcyUyMHJlcGFpciUyMHRlY2huaWNpYW4lMjBzb2xkZXJpbmclMjBzbWFydHBob25lfGVufDB8fHx8MTc3MDA3NTM4NXww&ixlib=rb-4.1.0&q=85')` 
        }}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h2 className="font-chivo text-4xl font-bold text-white mb-4">
            Gérez vos réparations en toute simplicité
          </h2>
          <p className="text-white/70 text-lg max-w-md">
            Suivi des réparations, gestion des stocks, facturation automatique et notifications clients.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <img 
              src="/logo.png" 
              alt="AMCPhone" 
              className="w-12 h-12 rounded-md object-contain"
            />
            <div>
              <h1 className="font-chivo font-bold text-2xl text-foreground">AMCPhone</h1>
              <p className="text-sm text-muted-foreground">Martinique</p>
            </div>
          </div>

          <h2 className="font-chivo text-3xl font-bold text-foreground mb-2">Connexion</h2>
          <p className="text-muted-foreground mb-8">
            Connectez-vous à votre compte pour continuer
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="vous@exemple.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="bg-secondary/50 border-border focus:border-primary"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground btn-animate"
            >
              {loading ? "Connexion..." : "Se connecter"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <p className="text-center text-muted-foreground mt-8">
            Pas encore de compte ?{" "}
            <Link 
              to="/register" 
              className="text-primary hover:underline"
              data-testid="register-link"
            >
              Créer un compte
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
